// Read-only answer pills for the admin review list: which options does an
// option-based question offer, and which did the company select? Returns null
// whenever pills don't apply (non-option types, unanswered or odd-shaped
// answers) so the caller falls back to its existing text rendering.

import type { Json, Question } from './types.ts';

export type AnswerOptionPill = { label: string; selected: boolean };

export function answerOptionPills(
  question: Pick<Question, 'type' | 'options'>,
  answer: Json | null | undefined,
): AnswerOptionPill[] | null {
  if (question.type === 'yesno') {
    if (typeof answer !== 'boolean') return null;
    return [
      { label: 'Yes', selected: answer },
      { label: 'No', selected: !answer },
    ];
  }
  if (question.type === 'select') {
    if (typeof answer !== 'string' || answer === '') return null;
    return withStaleAnswers(question.options ?? [], [answer]);
  }
  if (question.type === 'multiselect' || question.type === 'multiselect_uploads') {
    if (!Array.isArray(answer)) return null;
    const values = answer.filter((v): v is string => typeof v === 'string');
    if (values.length === 0) return null;
    return withStaleAnswers(question.options ?? [], values);
  }
  return null;
}

// All defined options in authored order, then any answered values no longer
// among the options (edited/removed after submission) appended as selected —
// the reviewer must always see the real answer.
function withStaleAnswers(options: string[], values: string[]): AnswerOptionPill[] {
  const stale = values.filter((v) => !options.includes(v));
  return [
    ...options.map((o) => ({ label: o, selected: values.includes(o) })),
    ...stale.map((v) => ({ label: v, selected: true })),
  ];
}
